import { lazy, Suspense, useState } from "react";
import type { SessionStatus } from "../domain/session-schema";
import { ClassroomOptions, ClassroomPlayback } from "./ClassroomPlayback";
import { ClassroomQuestions } from "./ClassroomQuestions";
import { LessonPanel } from "./LessonPanel";
import { liveSessionKey } from "./learner-profile";
import { useClassroom } from "./useClassroom";

const LiveClassroom = lazy(() =>
  import("./LiveClassroom").then((module) => ({ default: module.LiveClassroom })),
);

/** The call screen before anything is known: dark stage, a placeholder avatar, and one line. */
export function CallSplash({
  message,
  detail,
  waiting = false,
  onRetry,
}: {
  readonly message: string;
  readonly detail?: string;
  /** The page is still loading: the avatar rings. */
  readonly waiting?: boolean;
  readonly onRetry?: () => void;
}) {
  return (
    <div className="call" data-phase="splash">
      <div className="call-stage">
        <div className="call-overlay" data-visible="true">
          <div className="call-scroll">
            <div className="call-card">
              <div className="call-avatar" data-ringing={waiting}>
                <span aria-hidden="true">AI</span>
              </div>
              <p className="call-state" role={onRetry ? "alert" : "status"}>
                <strong>{message}</strong>
                {detail && <span>{detail}</span>}
              </p>
              {onRetry && (
                <button type="button" className="button primary" onClick={onRetry}>
                  다시 시도
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const statusLabels = {
  created: "Ready to begin",
  prewarming: "Preparing lesson",
  ready: "Ready to begin",
  narrating: "Reading lesson",
  yielding: "Pausing for your question",
  interrupting: "Keeping your place",
  listening: "Ready for your question",
  answering: "Reading answer",
  resuming: "Returning to the lesson",
  paused: "Lesson paused",
  recovering: "Reconnecting",
  complete: "Session complete",
  failed: "Lesson needs recovery",
} satisfies Record<SessionStatus, string>;

function rehearsalRequested() {
  return new URLSearchParams(window.location.search).has("rehearsal");
}

export function Classroom() {
  const classroom = useClassroom();
  const { demo, session, pending, error } = classroom;
  const [mode, setMode] = useState<"hybrid" | "all-live">("hybrid");
  const [scriptVisible, setScriptVisible] = useState(true);
  const [questionOpen, setQuestionOpen] = useState(false);
  if (!demo)
    return error ? (
      <CallSplash
        message="강의실을 열지 못했어요. 잠시 후 다시 시도해 주세요."
        onRetry={() => void classroom.loadDemo()}
      />
    ) : (
      <CallSplash message="강의실을 여는 중…" waiting />
    );

  if (
    demo.readiness.live.available ||
    demo.variants.some((variant) => variant.available) ||
    sessionStorage.getItem(liveSessionKey())
  )
    return (
      <Suspense fallback={<CallSplash message="강의실을 여는 중…" waiting />}>
        <LiveClassroom demo={demo} />
      </Suspense>
    );
  // The English text rehearsal is a development tool: learners only reach it through an explicit
  // `?rehearsal` link. Everyone else is told, in Korean, that the lesson is not ready yet.
  if (!demo.readiness.rehearsal.available || !rehearsalRequested())
    return (
      <CallSplash message="지금은 수업을 시작할 수 없어요" detail="강의실을 준비하고 있어요." />
    );
  const lesson = session?.lesson ?? demo.lesson;
  const completed = session?.committedCursor.phraseIndex ?? 0;
  const status = session ? statusLabels[session.status] : "Ready when you are";

  return (
    <div className="page" lang="en">
      <div className="page-header">
        <div>
          <p className="eyebrow">THE KOREAN CLASSROOM</p>
          <h1 lang="ko">{lesson.title}</h1>
          <p className="muted">
            With {demo.profile.name}
            <span className="inline-divider" />
            Read a little. Ask a question. Pick up where you left off.
          </p>
        </div>
        <span className="status warning">Text-only rehearsal</span>
      </div>
      <div className="rehearsal-note">
        <strong>A development rehearsal</strong>
        <span>
          Explore the lesson and interruption flow with text. Video, voice, and microphone are
          unavailable.
        </span>
      </div>
      {error && (
        <div className="notice error" role="alert">
          <p>{error}</p>
          <div className="cluster">
            {session && (
              <button
                type="button"
                className="button"
                disabled={pending}
                onClick={() => void classroom.reconnect()}
              >
                Reconnect to lesson
              </button>
            )}
            <button
              type="button"
              className="button"
              disabled={pending}
              onClick={() => void classroom.start(mode)}
            >
              Start new rehearsal
            </button>
          </div>
        </div>
      )}
      <div className={`classroom-grid ${scriptVisible ? "" : "script-hidden"}`}>
        <div className="lesson-column">
          <section className="lesson-stage" aria-labelledby="stage-heading">
            <div className="stage-top">
              <span className="stage-kicker">YOUR INSTRUCTOR</span>
              <span className="stage-status" role="status">
                {pending ? "Updating lesson…" : status}
              </span>
            </div>
            <div className="stage-content">
              {demo.profile.portraitUrl ? (
                <img
                  className="instructor-portrait"
                  src={demo.profile.portraitUrl}
                  alt={demo.profile.name}
                  width="640"
                  height="640"
                />
              ) : (
                <div className="stage-title">
                  <span className="stage-index">
                    {String(Math.min(completed + 1, lesson.phrases.length)).padStart(2, "0")} /{" "}
                    {String(lesson.phrases.length).padStart(2, "0")}
                  </span>
                  <h2 id="stage-heading">
                    A moment
                    <br />
                    to understand.
                  </h2>
                  <p>{demo.profile.name}</p>
                </div>
              )}
            </div>
            <div className="stage-bottom">
              <span>Portrait not yet available</span>
              <span>No media playing</span>
            </div>
          </section>
          <ClassroomPlayback
            classroom={classroom}
            mode={mode}
            scriptVisible={scriptVisible}
            onAsk={() => setQuestionOpen(true)}
            onShowScript={() => setScriptVisible(true)}
          />
          <ClassroomQuestions
            classroom={classroom}
            questionOpen={questionOpen}
            onClose={() => setQuestionOpen(false)}
          />
          <ClassroomOptions classroom={classroom} mode={mode} onModeChange={setMode} />
        </div>
        {scriptVisible && (
          <LessonPanel
            lesson={lesson}
            session={session}
            onCollapse={() => setScriptVisible(false)}
          />
        )}
      </div>
    </div>
  );
}
